import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import DashboardLayout from './DashboardLayout';
import styles from './CFODashboard.module.css';
import { 
  FiDollarSign, FiTrendingUp, FiPieChart, FiBarChart2,
  FiArrowUp, FiArrowDown, FiCheckCircle, FiAlertCircle,
  FiDownload, FiCalendar, FiBriefcase, FiTarget,
  FiPieChart as FiPie, FiCreditCard, FiTrendingDown
} from 'react-icons/fi';

const CFODashboard = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [financialSummary, setFinancialSummary] = useState({});
  const [revenueBreakdown, setRevenueBreakdown] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [cashFlow, setCashFlow] = useState([]);
  const [budgetVariance, setBudgetVariance] = useState([]);

  useEffect(() => {
    fetchUser();
    fetchDashboardData();
  }, []);

  const fetchUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      setUser(profile);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      setFinancialSummary({
        revenue: 63800000,
        expenses: 41200000,
        profit: 22600000,
        margin: 35.4,
        cashReserves: 28400000,
        debtRatio: 18.5,
        ebitda: 18600000
      });

      setRevenueBreakdown([
        { source: 'Technology Solutions', amount: 24500000, percentage: 38, growth: '+32%' },
        { source: 'Enterprise Services', amount: 18200000, percentage: 29, growth: '+28%' },
        { source: 'Cloud & AI', amount: 12800000, percentage: 20, growth: '+45%' },
        { source: 'Consulting', amount: 8300000, percentage: 13, growth: '+22%' }
      ]);

      setExpenseCategories([
        { category: 'Salaries', amount: 18500000, percentage: 45, trend: 'up' },
        { category: 'Infrastructure', amount: 8200000, percentage: 20, trend: 'stable' },
        { category: 'Marketing', amount: 6200000, percentage: 15, trend: 'up' },
        { category: 'R&D', amount: 5300000, percentage: 13, trend: 'up' },
        { category: 'Operations', amount: 3000000, percentage: 7, trend: 'down' }
      ]);

      setCashFlow([
        { month: 'Jul', inflow: 5.8, outflow: 4.2, net: 1.6 },
        { month: 'Aug', inflow: 6.2, outflow: 4.5, net: 1.7 },
        { month: 'Sep', inflow: 6.5, outflow: 4.8, net: 1.7 },
        { month: 'Oct', inflow: 6.8, outflow: 5.0, net: 1.8 }
      ]);

      setBudgetVariance([
        { department: 'Sales', budget: 5000000, actual: 4850000, variance: 3.0, status: 'good' },
        { department: 'Marketing', budget: 3200000, actual: 3450000, variance: -7.8, status: 'warning' },
        { department: 'Engineering', budget: 8000000, actual: 7850000, variance: 1.9, status: 'good' },
        { department: 'Operations', budget: 4500000, actual: 4680000, variance: -4.0, status: 'warning' },
        { department: 'R&D', budget: 5500000, actual: 5400000, variance: 1.8, status: 'good' }
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="CFO">
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="CFO">
      <div className={styles.cfoDashboard}>
        <div className={styles.header}>
          <div>
            <h1>CFO Dashboard</h1>
            <p>Financial Performance & Strategic Planning</p>
          </div>
          <button className={styles.exportBtn}>
            <FiDownload /> Export Financials
          </button>
        </div>

        {/* Financial Summary Cards */}
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryIcon} style={{ background: '#1a73e8' }}>
              <FiDollarSign />
            </div>
            <div className={styles.summaryInfo}>
              <h3>Total Revenue</h3>
              <p className={styles.summaryValue}>₹{(financialSummary.revenue / 1000000).toFixed(1)}M</p>
              <span className={styles.positive}>+28.5% YoY</span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryIcon} style={{ background: '#0f9d58' }}>
              <FiTrendingUp />
            </div>
            <div className={styles.summaryInfo}>
              <h3>Net Profit</h3>
              <p className={styles.summaryValue}>₹{(financialSummary.profit / 1000000).toFixed(1)}M</p>
              <span className={styles.positive}>Margin: {financialSummary.margin}%</span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryIcon} style={{ background: '#e37400' }}>
              <FiTarget />
            </div>
            <div className={styles.summaryInfo}>
              <h3>EBITDA</h3>
              <p className={styles.summaryValue}>₹{(financialSummary.ebitda / 1000000).toFixed(1)}M</p>
              <span>Margin: 29.2%</span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryIcon} style={{ background: '#d93025' }}>
              <FiCreditCard />
            </div>
            <div className={styles.summaryInfo}>
              <h3>Cash Reserves</h3>
              <p className={styles.summaryValue}>₹{(financialSummary.cashReserves / 1000000).toFixed(1)}M</p>
              <span>Debt Ratio: {financialSummary.debtRatio}%</span>
            </div>
          </div>
        </div>

        {/* Revenue & Expense Breakdown */}
        <div className={styles.twoColumnGrid}>
          <div className={styles.sectionCard}>
            <h2><FiPie /> Revenue by Business Unit</h2>
            <div className={styles.breakdownList}>
              {revenueBreakdown.map((item, index) => (
                <div key={index} className={styles.breakdownItem}>
                  <div className={styles.breakdownInfo}>
                    <span>{item.source}</span>
                    <span>₹{(item.amount / 1000000).toFixed(1)}M</span>
                    <span className={styles.growthPositive}>{item.growth}</span>
                  </div>
                  <div className={styles.breakdownBar}>
                    <div className={styles.bar} style={{ width: `${item.percentage}%` }}></div>
                  </div>
                  <div className={styles.breakdownPercent}>{item.percentage}%</div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.sectionCard}>
            <h2><FiBarChart2 /> Expense Categories</h2>
            <div className={styles.breakdownList}>
              {expenseCategories.map((item, index) => (
                <div key={index} className={styles.breakdownItem}>
                  <div className={styles.breakdownInfo}>
                    <span>{item.category}</span>
                    <span>₹{(item.amount / 1000000).toFixed(1)}M</span>
                    <span className={item.trend === 'up' ? styles.trendUp : item.trend === 'down' ? styles.trendDown : styles.trendStable}>
                      {item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '→'}
                    </span>
                  </div>
                  <div className={styles.breakdownBar}>
                    <div className={styles.barExpense} style={{ width: `${item.percentage}%` }}></div>
                  </div>
                  <div className={styles.breakdownPercent}>{item.percentage}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cash Flow Forecast */}
        <div className={styles.sectionCard}>
          <h2><FiCalendar /> Cash Flow Forecast</h2>
          <div className={styles.cashFlowChart}>
            <div className={styles.chartHeader}>
              <span>Month</span>
              <span>Inflow (₹M)</span>
              <span>Outflow (₹M)</span>
              <span>Net (₹M)</span>
            </div>
            {cashFlow.map((month, index) => (
              <div key={index} className={styles.cashFlowRow}>
                <span className={styles.month}>{month.month}</span>
                <span className={styles.inflow}>₹{month.inflow}M</span>
                <span className={styles.outflow}>₹{month.outflow}M</span>
                <span className={month.net >= 0 ? styles.positive : styles.negative}>
                  ₹{month.net}M
                  {month.net >= 0 ? <FiArrowUp /> : <FiArrowDown />}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Budget Variance */}
        <div className={styles.sectionCard}>
          <h2><FiBriefcase /> Budget Variance Analysis</h2>
          <div className={styles.varianceTable}>
            <div className={styles.tableHeader}>
              <span>Department</span>
              <span>Budget (₹)</span>
              <span>Actual (₹)</span>
              <span>Variance</span>
              <span>Status</span>
            </div>
            {budgetVariance.map((item, index) => (
              <div key={index} className={styles.tableRow}>
                <span>{item.department}</span>
                <span>₹{(item.budget / 1000000).toFixed(1)}M</span>
                <span>₹{(item.actual / 1000000).toFixed(1)}M</span>
                <span className={item.variance >= 0 ? styles.positive : styles.negative}>
                  {item.variance >= 0 ? '+' : ''}{item.variance}%
                </span>
                <span>
                  {item.status === 'good' ? 
                    <FiCheckCircle className={styles.successIcon} /> : 
                    <FiAlertCircle className={styles.warningIcon} />
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CFODashboard;